import { usePermissions } from './usePermissions'

/**
 * Hook para verificar permissões de campos e ações específicas
 * Retorna funções auxiliares para verificar se usuário pode editar/visualizar campos
 */
export const useFieldPermissions = () => {
  const { hasPermission, loading } = usePermissions()

  /**
   * Verifica se usuário pode editar um campo específico
   * @param permission - Permissão necessária (ex: 'clientes.dados_pessoais.editar')
   * @returns true se pode editar, false caso contrário
   */
  const canEdit = (permission: string): boolean => {
    return hasPermission(permission)
  }

  /**
   * Verifica se usuário pode visualizar um campo/seção
   * @param permission - Permissão necessária (ex: 'clientes.dados_pessoais.visualizar')
   * @returns true se pode visualizar, false caso contrário
   */
  const canView = (permission: string): boolean => {
    return hasPermission(permission)
  }

  /**
   * Retorna props para input baseado em permissão de edição
   * @param editPermission - Permissão de edição
   * @returns objeto com readOnly e className apropriados
   */
  const getInputProps = (editPermission: string) => {
    const canEditField = canEdit(editPermission)
    return {
      readOnly: !canEditField,
      disabled: !canEditField,
      className: !canEditField 
        ? 'bg-gray-100 cursor-not-allowed opacity-75' 
        : ''
    }
  }

  /**
   * Verifica se deve mostrar botão de ação
   * @param permission - Permissão necessária
   * @returns true se deve mostrar, false caso contrário
   */
  const shouldShowAction = (permission: string): boolean => {
    return hasPermission(permission)
  }

  return {
    canEdit,
    canView,
    getInputProps,
    shouldShowAction,
    loading
  }
}
